import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import type { ProcessType, StartProcessInput } from '@pms/shared';
import { usersService } from '@/services/users.service';
import { recruitmentService } from '@/services/recruitment.service';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { DatePicker } from '@/components/ui/date-picker';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { getErrorMessage } from './utils';

export function StartProcessModal({
  processType, onClose, onStarted,
}: {
  processType: ProcessType;
  onClose: () => void;
  onStarted: (instanceId: string) => void;
}) {
  const queryClient = useQueryClient();
  const [userId, setUserId] = useState('');
  const [joiningDate, setJoiningDate] = useState('');

  const usersQuery = useQuery({
    queryKey: ['staff', { limit: 100, is_active: true }],
    queryFn: () => usersService.list({ limit: 100, is_active: true }),
  });

  const start = useMutation({
    mutationFn: () => {
      const payload: StartProcessInput = {
        user_id: userId,
        process_type: processType,
        joining_date: processType === 'joining' ? joiningDate : null,
      };
      return recruitmentService.startProcess(payload);
    },
    onSuccess: (res) => {
      toast.success(processType === 'joining' ? 'Onboarding started' : 'Exit process started');
      void queryClient.invalidateQueries({ queryKey: ['recruitment'] });
      onStarted(res.id);
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const canSubmit = userId && (processType !== 'joining' || joiningDate);

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{processType === 'joining' ? 'Start Onboarding' : 'Start Exit Process'}</DialogTitle>
          <p className="text-sm text-muted-foreground">Select an employee to begin the {processType} process</p>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>Employee <span className="text-destructive">*</span></Label>
            <Select value={userId || undefined} onValueChange={setUserId}>
              <SelectTrigger><SelectValue placeholder="Choose an employee…" /></SelectTrigger>
              <SelectContent>
                {(usersQuery.data?.data ?? []).map((u) => (
                  <SelectItem key={u.id} value={u.id}>
                    {u.first_name} {u.last_name}{u.employee_code ? ` (${u.employee_code})` : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {processType === 'joining' && (
            <div className="space-y-1.5">
              <Label>Joining Date <span className="text-destructive">*</span></Label>
              <DatePicker value={joiningDate} onChange={setJoiningDate} />
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
          <Button type="button" disabled={!canSubmit || start.isPending} onClick={() => start.mutate()}>
            {start.isPending ? 'Starting…' : 'Start Process'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
